import React from "react";

export type RouteItem = {
  path: string;
  name: string;
  icon: string;
  component: React.ComponentType<any>;
  layout: "/admin" | "/auth";
  isSidebarItem: boolean;
  allow?: UserPermissions[];
};

export enum DAChatType {
  ScreenerBot = "ScreenerBot",
  CodeGen = "CodeGen",
}

export enum ExecutionInputType {
  text = "text",
  file = "file",
  url = "url",
}

export type User = {
  id: number;
  name: string;
  email: string;
  roles: UserPermissions[];
  createdBy?: string;
};

export enum BulkQnaModelDisplayName {
  // gpt35 = "Gpt 3.5",
  // gpt4O = "Gpt 4O",
  gpt53 = "Gpt 5.3",
}

export enum UserPermissions {
  admin = "admin",
  read = "read",
  write = "write",
  bulkqna = "bulkqna",
  dataanalysis = "dataanalysis",
  betauser = "betauser",
  translator = "translator",
  internalLLM = "internalllm",
}

export type LoginResp = {
  token: string;
  user: User;
};

export type UnbilledMonthlyReportResponse = {
  year: number;
  month: number;
  sum: number;
}[];

export type FollowUpsMonthlyReportResponse = UnbilledMonthlyReportResponse;

export type AdminUserHeaderInfoResponse = {
  total: number;
};

export type AdminUsersList = {
  data: User[];
  total: number;
};

export type HeaderAggDataResponse = {
  numExecs: number;
  numQues?: number;
};

export enum ExecutionStatus {
  error = "error",
  parsed = "parsed",
  qna_processed = "qna_processed",
}

export type ChemicalSearchResponse = {
  data: {
    chemicalCAS: string;
    chemicalName: string;
    chemicalSynonyms?: string;
    executionStatus: ExecutionStatus;
    executionTimeTakenInSeconds?: number;
    executionStartedAt: string;
    userName: string;
    userId: number;
    groupName: string;
    groupId: number;
    executionId: number;
    chemicalId: number;
  }[];
  total: number;
};

export type ChemicalInfoResponse = {
  cas: string;
  id: number;
  name: string;
  synonyms?: string;
  executionIds: number[];
};

export type AllChemicalExecutionsResponse = {
  data: {
    status: ExecutionStatus;
    timeTakenInSeconds?: number;
    startedAt: string;
    userName: string;
    userId: number;
    executionId: number;
    chemicalName: string;
    chemicalCAS: string;
    chemicalId: number;
  }[];
  total: number;
};

export enum SearchResultStatus {
  error = "error",
  success = "success",
}

export type ChemicalExecutionInfoResponse = {
  total: number;
  data: {
    websiteId: number;
    websiteUrl: string;
    searchTerm?: string;
    found: boolean;
    searchStatus: SearchResultStatus;
    errorMessage?: string;
    screenshotUrl?: string;
    pageUrl: string;
    startedAt: string;
    searchTimeTakenInSeconds?: number;
  }[];
  executionInfo?: {
    executionId: number;
    chemicalSynonymsUsedForSearch?: string | undefined;
    timeTakenInSeconds?: number | undefined;
    status: ExecutionStatus;
    startedAt: string;
    groupId: number;
    groupName: string;
  };
};

export type GroupInfoResponse = {
  name: string;
  id: number;
  websites: { id: number; url: string }[];
  userName: string;
  userId: number;
  lastEditedBy: string;
  lastEditedById: number;
  createdAt: string;
  updatedAt: string;
};

export type AllGroupsResponse = {
  data: {
    lastEditedBy: string;
    lastEditedById: number;
    user: string;
    userId: string;
    name: string;
    id: number;
    createdAt: string;
    websites: { id: number; url: string }[];
  }[];
  total: number;
};

export type AllWebsitesResponse = {
  id: number;
  url: string;
}[];

export type GroupSuggestionsResponse = {
  name: string;
  id: number;
}[];

export type ChemicalSuggestionsResponse = {
  cas: string;
  name: string;
  synonyms?: string;
  id: number;
}[];

export type StartSummarizeResponse = {
  jobid: string;
};

export enum SummarizationStatus {
  error = "error",
  completed = "completed",
  running = "running",
}

export type SummarizeInfoResponse = {
  status: SummarizationStatus;
  url: string;
  model?: string;
  summaryLen?: number;
  data?: {
    extractedText?: string;
    keySentences?: string[];
    abstractiveSummary?: string;
    keyPhrases?: string[];
  };
};

export type SearchResult = {
  id: number;
  executionId: number;
  url: string;
  extractedText?: string;
  snapshotUrl?: string;
  isPdf?: boolean;
  downloadedFileURL?: string;
  themes?: string[];
  // nerHtml?: string;
  engine: SearchEngine;
  date?: string;
  title?: string;
  keySentences?: string[];
  snippet?: string;
  urlScore?: number;
  titleScore?: number;
  extractedTextScore?: number;
  error?: string;
};

export type NerWords = {
  person?: [string, number][];
  org?: [string, number][];
  gpe?: [string, number][];
  date?: [string, number][];
  money?: [string, number][];
};

export type ExecutionInfoResponse = Omit<ExecutionType, "question">;

export type GetQuestionsResponse = QuestionType[];

export type PastExecutionsResponse = {
  data: {
    inputType: ExecutionInputType;
    numQues: number;
    createdAt: string;
    isConfidential: boolean;
    id: number;
    status: ExecutionStatus;
    user: {
      name: string;
    };
    question?: QuestionType;
    numWords?: number;
    numSentences?: number;
    numFiles?: number;
    numUrls?: number;
  }[];
  total: number;
};

export enum BulkQnaStatus {
  error = "error",
  queued = "queued",
  processing = "processing",
  completed = "completed",
}

export type BulkExtractResponse = {
  data: {
    id: number;
    createdAt: string;
    inputfileUrl: string;
    outputfileUrl: string;
    status: string;
    createdBy: string;
    totalExec?: number;
    currentExec?: number;
  }[];
  total: number;
};

export type ScreeningResponse = {
  data: {
    id: number;
    createdAt: string;
    inputfileUrl: string;
    outputFileUrl: string;
    status: string;
    createdBy: string;
    updatedAt: string;
    query: string;
  }[];
  total: number;
};

export type TranslationResponse = {
  data: {
    id: number;
    createdAt: string;
    input: string;
    output: string;
    status: string;
    createdBy: string;
    inputType: string;
    errorText?: string;
  }[];
  total: number;
};

export type ImageIntellGetAllExecutionsResponse = {
  data: {
    id: number;
    images: string[];
    userName: string;
    userEmail: string;
    userId: number;
    createdAt: string;
  }[];
  total: number;
};

export type BulkQnasResponse = {
  data: {
    id: number;
    name: string;
    user: string;
    inputFile: string;
    outputFile?: string;
    createdAt: string;
    status: BulkQnaStatus;
    totalExec?: number;
    currentExec?: number;
    cost?: number;
    endedAt?: string;
    startedAt?: string;
    model?: string;
  }[];
  total: number;
};

export type DocStructuringResponse = {
  data: {
    id: number;
    name: string;
    user: string;
    inputFiles: string[];
    outputFile?: string;
    createdAt: string;
    status: BulkQnaStatus;
    message?: string;
    totalExec?: number;
    currentExec?: number;
    endedAt?: string;
    startedAt?: string;
  }[];
  total: number;
};

export type CurrentExecutionsResponse = {
  data: {
    id: number;
    status: ExecutionStatus;
    startedAt?: string;
    searchTerm: string;
    numPages: string;
    user: {
      name: string;
    };
  }[];
  total: number;
};

export enum SearchEngine {
  google = "google",
  yahoo = "yahoo",
  duckduckgo = "duckduckgo",
  bing = "bing",
  googlenews = "googlenews",
}

export type SearchPdfResponse = (SearchResult & {
  pageNumber: number;
  screenshotFileURL: string;
  inputFileURL: string;
})[];

export enum AlertStatus {
  active = "active",
  inactive = "inactive",
}

export type AlertData = {
  id: number;
  userId: number;
  executionId: number;
  status: AlertStatus;
  freqDays: number;
  nextRunAt: string;
  otherEmails: string[];
  updatedAt: string;
  createdAt: string;
  searchTerm: string;
  numPages: number;
  location?: {
    loc: string;
    country: string;
  };
  startDate: string;
  startDateFilter?: string;
  endDateFilter?: string;
  searchEngines: SearchEngine[];
  name: string;
};

export type UpcomingExecutionsResponse = (AlertData & {
  executionDate: string;
})[];

export type AllAlertsResponse = {
  data: AlertData[];
  total: number;
};

export type ExecutionReportResponse = {
  execsByDate: {
    count: number;
    day: string;
  }[];
  quesByDate: {
    count: number;
    day: string;
  }[];
};

export enum QuestionStatus {
  error = "error",
  answered = "answered",
}

export type ImageQuestionType = {
  id: number;
  executionId: number;
  question: string;
  cost: number;
  messages: {
    text: string;
    type: string;
  };
};

export type QuestionType = {
  id: number;
  executionId: number;
  question: string;
  status: QuestionStatus;
  callFrom?: string;
  qnaResponse?: {
    responses: string[];
    sources: {
      value: string;
      index: number;
      original_value: string;
      page_no: number;
      url: string;
    }[][];
  };
  cost?: number;
  redactedHtml: string;
  timeTakenInSeconds?: number;
  redactionMapping?: { [key: string]: string };
};

export type ExecutionType = {
  id: number;
  inputType: ExecutionInputType;
  userId: number;
  user: {
    name: string;
  };
  inputs: {
    executionId?: number;
    extractedText: {
      text: Record<string, string>;
      url: string;
    };
    numSentences?: number;
    numWords?: number;
    inputType?: string;
    inputText?: string;
  }[];
  status: ExecutionStatus;
  isConfidential?: boolean;
  inputText?: string[];
  questions?: QuestionType[];
  redactedText?: string[];
  redactedHTML?: string[];
  updatedAt?: string;
  createdAt?: string;
  wc2Words?: Record<string, number>;
  wc3Words?: Record<string, number>;
  keyphrases?: string[];
  numWords?: number;
  numSentences?: number;
};

export type ExtractedText = {
  extractedText: { [key: string]: string };
};

export type ExecutionSaveResponse = ExecutionType;

export type AskQnAResponse = ExecutionType;

export type AnalyseExecutionResponse = Pick<
  ExecutionType,
  "wc2Words" | "wc3Words" | "keyphrases"
>;

export type Chat = {
  _id: string;
  sender: string;
  text: string;
  time: string;
};

export type OldChat = {
  _id: string;
  description: string;
  chat: Chat[];
  createdBy: string;
  createdAt: string;
};

export type ChatMessages = {
  content: string;
  role: "user" | "assistant";
};

export type ConversationAllResponse = {
  conversations: {
    id: any;
    createdAt: any;
    user: {
      name: any;
      id: any;
    };
    containerStatus: any;
    name: any;
    messageCount: number;
    cost?: number;
    conversationType: string;
  }[];
  total: number;
};

export type NewConversationResponse = {
  id: number;
  containerStatus: "running" | "stopped" | "error";
};

export type GetConversationMessagesResponse = {
  messages: {
    id: number;
    createdAt: Date;
    content: string | undefined;
    files:
      | {
          name: string;
          url: string;
        }[]
      | undefined;
    role: "user" | "assistant";
  }[];
  hasMore: boolean;
};

export type ImageIntellResponse = {
  id: any;
  images: string[];
  createdAt: string;
  userName: string;
  questions: {
    id: number;
    userId: number;
    executionId: number;
    question: string;
    response: {
      messages: {
        text: string;
        type: string;
      };
      cost: number;
    };
    updatedAt: string;
    createdAt: string;
  }[];
};

export type ConversationResponse = {
  id: any;
  createdAt: any;
  user: {
    name: any;
    id: any;
  };
  containerStatus: any;
  name: any;
  messageCount: number;
};
